import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';

import ProcessorCard from './components/ProcessorCard';
import ConfigurationModal from './components/ConfigurationModal';
import TestPaymentModal from './components/TestPaymentModal';
import { useAuth } from '../../contexts/AuthContext';

const PaymentProcessorsManagement = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [processors, setProcessors] = useState([
    {
      id: 'stripe',
      name: 'Stripe',
      icon: 'CreditCard',
      status: 'active',
      configured: true,
      description: 'Accept credit cards, digital wallets, and more',
      features: ['Credit Cards', 'Apple Pay', 'Google Pay', 'ACH', 'SEPA'],
      fees: '2.9% + $0.30 per transaction',
      supportedCurrencies: ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'INR', 'JPY'],
      apiKey: 'pk_test_***************',
      webhookUrl: 'https://yourapp.com/webhooks/stripe',
      testMode: true
    },
    {
      id: 'paypal',
      name: 'PayPal',
      icon: 'Wallet',
      status: 'inactive',
      configured: false,
      description: 'Accept PayPal, Venmo, and credit cards',
      features: ['PayPal', 'Venmo', 'Credit Cards', 'Pay Later'],
      fees: '2.99% + $0.49 per transaction',
      supportedCurrencies: ['USD', 'EUR', 'GBP', 'CAD', 'AUD'],
      apiKey: null,
      webhookUrl: null,
      testMode: false
    },
    {
      id: 'square',
      name: 'Square',
      icon: 'Square',
      status: 'inactive',
      configured: false,
      description: 'In-person and online payments',
      features: ['Credit Cards', 'Tap to Pay', 'Invoicing', 'Gift Cards'],
      fees: '2.6% + $0.10 per transaction',
      supportedCurrencies: ['USD', 'CAD', 'GBP', 'AUD', 'JPY'],
      apiKey: null,
      webhookUrl: null,
      testMode: false
    },
    {
      id: 'razorpay',
      name: 'Razorpay',
      icon: 'Zap',
      status: 'inactive',
      configured: false,
      description: 'Payment gateway for India',
      features: ['UPI', 'Cards', 'Net Banking', 'Wallets', 'EMI'],
      fees: '2% per transaction',
      supportedCurrencies: ['INR'],
      apiKey: null,
      webhookUrl: null,
      testMode: false
    }
  ]);

  const [selectedProcessor, setSelectedProcessor] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/authentication');
      return;
    }
  }, [user, authLoading, navigate]);

  const handleConfigureProcessor = (processor) => {
    setSelectedProcessor(processor);
    setShowConfigModal(true);
  };

  const handleTestProcessor = (processor) => {
    setSelectedProcessor(processor);
    setShowTestModal(true);
  };

  const handleToggleProcessor = (processorId) => {
    setProcessors(processors?.map(p => 
      p?.id === processorId 
        ? { ...p, status: p?.status === 'active' ? 'inactive' : 'active' }
        : p
    ));
  };

  const handleSaveConfiguration = (config) => {
    setProcessors(processors?.map(p => 
      p?.id === selectedProcessor?.id 
        ? { ...p, ...config, configured: true }
        : p
    ));
    setShowConfigModal(false);
  };

  const filteredProcessors = processors?.filter(p => {
    const matchesSearch = p?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         p?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesStatus = filterStatus === 'all' || p?.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const activeProcessors = processors?.filter(p => p?.status === 'active')?.length;
  const totalTransactions = 1247;
  const totalRevenue = 45678.90;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <NavigationBar stats={{
        activeProcessors,
        totalTransactions,
        totalRevenue
      }}>
      <div className="flex-1 p-6 lg:p-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Payment Processors</h1>
              <p className="text-muted-foreground mt-1">
                Manage and configure your payment gateways
              </p>
            </div>
            <Button
              iconName="Plus"
              onClick={() => setShowConfigModal(true)}
            >
              Add Processor
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Active Processors</span>
              <Icon name="CheckCircle2" size={20} className="text-green-600" />
            </div>
            <p className="text-3xl font-bold text-foreground">{activeProcessors}</p>
            <p className="text-xs text-muted-foreground mt-1">
              {processors?.length} total configured
            </p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Transactions</span>
              <Icon name="TrendingUp" size={20} className="text-blue-600" />
            </div>
            <p className="text-3xl font-bold text-foreground">{totalTransactions?.toLocaleString()}</p>
            <p className="text-xs text-green-600 mt-1">
              +12.5% from last month
            </p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Total Revenue</span>
              <Icon name="DollarSign" size={20} className="text-primary" />
            </div>
            <p className="text-3xl font-bold text-foreground">
              ${totalRevenue?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-xs text-green-600 mt-1">
              +8.3% from last month
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Input
              placeholder="Search processors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              iconName="Search"
            />
          </div>
          <Select
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' }
            ]}
            value={filterStatus}
            onChange={setFilterStatus}
          />
        </div>

        {/* Processors Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {filteredProcessors?.map(processor => (
            <ProcessorCard
              key={processor?.id}
              processor={processor}
              onConfigure={handleConfigureProcessor}
              onTest={handleTestProcessor}
              onToggle={handleToggleProcessor}
            />
          ))}
        </div>

        {filteredProcessors?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No processors found matching your criteria</p>
          </div>
        )}
      </div>

      {/* Configuration Modal */}
      {showConfigModal && (
        <ConfigurationModal
          isOpen={showConfigModal}
          onClose={() => setShowConfigModal(false)}
          processor={selectedProcessor}
          onSave={handleSaveConfiguration}
        />
      )}

      {/* Test Payment Modal */}
      {showTestModal && (
        <TestPaymentModal
          isOpen={showTestModal}
          onClose={() => setShowTestModal(false)}
          processor={selectedProcessor}
        />
      )}
      </NavigationBar>
    </div>
  );
};

export default PaymentProcessorsManagement;